import {
    getDomainName,
    listDomainNames,
    lookupHostname
} from './queries';
import {
    createDomainName,
    deleteDomainName,
    updateDomainName,
} from './mutations';

import {
    generateClient,
    GraphQLQuery,
} from 'aws-amplify/api';
import { DomainName } from "../types.ts";
import {
    showErrorNotifications,
    showSuccessNotification
} from "../../../components/ServiceNotifications.tsx";


export type ListDomainNames = {
    listDomainNames: { items: DomainName[] }
}

export type GetDomainName = {
    getDomainName: DomainName
}

export type CreateDomainName = {
    createDomainName: DomainName
}

export type UpdateDomainName = {
    updateDomainName: DomainName
}

export type DeleteDomainName = {
    deleteDomainName: string
}

// Client will be generated when needed to allow for proper mocking
const getClient = () => generateClient();

/**
 * Fetches domain name records with the provided filter.
 * 
 * This method uses a simplified approach where the filter is passed directly to the 
 * GraphQL query without any client-side manipulation. The backend GraphQL resolvers
 * handle all user isolation filtering automatically based on the authenticated user's
 * context, ensuring proper multi-tenant data security.
 * 
 * @param filter - Filter object to apply to the GraphQL query. This filter is passed
 *                 unchanged to the backend, which will combine it with user-based filtering.
 * @param userId - DEPRECATED: This parameter is no longer used. Backend handles user isolation automatically.
 * @returns Promise<DomainName[] | undefined> - Array of domain names filtered by the backend,
 *          or undefined if an error occurs.
 * 
 * @example
 * // Fetch all domain names for the current user (backend filters by user automatically)
 * const domains = await fetchRecords({});
 * 
 * // Fetch domain names with a specific filter (backend combines with user filtering)
 * const filteredDomains = await fetchRecords({ 
 *   domainName: { contains: 'example' } 
 * });
 */
export const fetchRecords = async (filter: object, userId?: string) => {
    // Show deprecation warning if userId is provided
    if (userId !== undefined) {
        console.warn('fetchRecords: userId parameter is deprecated and no longer used. Backend now handles user isolation filtering automatically.');
    }

    try {
        // Simplified approach: pass filter directly to GraphQL query
        // Backend GraphQL resolvers handle user isolation filtering automatically
        // No client-side filter manipulation needed
        const response = await getClient().graphql<GraphQLQuery<ListDomainNames>>({
            query: listDomainNames,
            variables: { filter },
        });
        return response.data?.listDomainNames.items;
    } catch (e) {
        console.debug(e);
        showErrorNotifications(e)
    }
};

export const fetch = async (id: string) => {
    try {
        const response = await getClient().graphql<GraphQLQuery<GetDomainName>>({
            query: getDomainName,
            variables: { id: id },
        });
        return response.data?.getDomainName;
    } catch (e) {
        showErrorNotifications(e)
    }
};

export const add = async (payload: Record<string, unknown>) => {

    try {
        // Backend automatically extracts user context from authentication token
        // No need to pass createdByUsername explicitly
        const response = await getClient().graphql<GraphQLQuery<CreateDomainName>>({
            query: createDomainName,
            variables: { input: payload },
        });
        console.debug('in the service response', response);

        showSuccessNotification();
        return response.data?.createDomainName;
    } catch (e) {
        console.trace(e);
        showErrorNotifications(e)
    }
};

export const update = async (id: string, record: DomainName) => {

    console.debug('IN UPDATE DOMAIN NAME', id, record);

    delete record.created;
    delete record.updated;

    try {
        const response = await getClient().graphql<GraphQLQuery<UpdateDomainName>>({
            query: (updateDomainName),
            variables: { input: record },
        });

        showSuccessNotification();
        return response.data?.updateDomainName;
    } catch (e) {
        console.trace(e);
        showErrorNotifications(e)
    }
};

export const deleteRecord = async (id: string) => {
    try {
        const response = await getClient().graphql<GraphQLQuery<DeleteDomainName>>({
            query: (deleteDomainName),
            variables: { input: { id: id } },

        });
        showSuccessNotification();
        return response.data?.deleteDomainName;
    } catch (e) {
        console.trace(e);
        showErrorNotifications(e)
    }
};

export const lookupDnsInformation = async (hostname: string) => {
    try {
        const response = await getClient().graphql<GraphQLQuery<any>>({
            query: (lookupHostname),
            variables: {
                hostname: hostname
            },

        });
        showSuccessNotification('DNS Lookup Successful');
        return response.data?.lookupHostname;
    } catch (e) {
        console.trace(e);
        showErrorNotifications(e)
    }
};
